import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import ItemCard from "../components/ItemCard";
import Marquee from "../components/Marquee";
import EmailCapture from "../components/EmailCapture";
import CuratorBadge from "../components/CuratorBadge";
import StyleQuiz from "../components/StyleQuiz";
import { ArrowRight, Sparkles, Recycle, Heart, HelpCircle } from "lucide-react";

export default function Home() {
  const [items, setItems] = useState([]);
  const [boggyItems, setBoggyItems] = useState([]);
  const [foxItems, setFoxItems] = useState([]);
  const [mascots, setMascots] = useState({});
  const [quizOpen, setQuizOpen] = useState(false);

  useEffect(() => {
    api.get("/items").then((r) => setItems(r.data.slice(0, 8))).catch(() => {});
    api.get("/items", { params: { curator: "boggy" } }).then((r) => setBoggyItems(r.data.slice(0, 4))).catch(() => {});
    api.get("/items", { params: { curator: "fox" } }).then((r) => setFoxItems(r.data.slice(0, 4))).catch(() => {});
    api.get("/mascots").then((r) => setMascots(r.data || {})).catch(() => {});
  }, []);

  const Boggy = mascots.boggy ? <img src={fileUrl(mascots.boggy)} alt="Boggy the frog" className="w-full h-full object-contain" /> : null;
  const Fox = mascots.fox ? <img src={fileUrl(mascots.fox)} alt="Fox the fox" className="w-full h-full object-contain" /> : null;

  return (
    <div data-testid="home-page">
      <EmailCapture />
      <StyleQuiz open={quizOpen} onClose={() => setQuizOpen(false)} />
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-7 relative">
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.9]">
            Boggy and Fox
            <br /> <span className="bg-yellow inline-block px-3 -rotate-1">Vintage Shop</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl max-w-xl">
            Hi! We're <strong>Boggy</strong> (the frog, top hand-picker) and <strong>Fox</strong> (the fox, the haggler).
            We curate second-hand fits worth wearing. Wild prices, direct payment, fast shipping.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/boggy" className="brut-btn bg-green px-4 py-2 text-sm" data-testid="meet-boggy-btn">Meet Boggy 🐸</Link>
            <Link to="/fox" className="brut-btn bg-orange text-white px-4 py-2 text-sm" data-testid="meet-fox-btn">Meet Fox 🦊</Link>
            <button onClick={() => setQuizOpen(true)} className="brut-btn bg-pink text-white px-4 py-2 text-sm flex items-center gap-1" data-testid="open-quiz-btn">
              <HelpCircle size={14} strokeWidth={3} /> Take the style quiz
            </button>
          </div>
          <div className="mt-6 flex flex-wrap gap-4">
            <Link to="/shop" className="brut-btn bg-orange text-white px-6 py-4 text-lg flex items-center gap-2" data-testid="hero-shop-btn">
              Hop the rack <ArrowRight size={20} strokeWidth={3} />
            </Link>
            <Link to="/signup" className="brut-btn bg-white px-6 py-4 text-lg" data-testid="hero-signup-btn">Join the den</Link>
          </div>
        </div>
        <div className="md:col-span-5 relative">
          <div className="grid grid-cols-2 gap-4">
            <div className="brut bg-cyan/30 aspect-square overflow-hidden p-2 -rotate-3 relative">
              {Boggy || <div className="w-full h-full bg-green flex items-center justify-center font-display text-3xl uppercase">Boggy</div>}
              <span className="tag-sticker bg-yellow absolute -bottom-3 left-3 text-xl rotate-tilt-l">Boggy</span>
            </div>
            <div className="brut bg-pink/20 aspect-square overflow-hidden p-2 rotate-3 mt-6 relative">
              {Fox || <div className="w-full h-full bg-orange flex items-center justify-center font-display text-3xl uppercase">Fox</div>}
              <span className="tag-sticker bg-cyan absolute -bottom-3 right-3 text-xl rotate-tilt-r">Fox</span>
            </div>
            <div className="brut aspect-square overflow-hidden -rotate-2 mt-4 relative" data-testid="hero-boggy-pick">
              <img
                src="https://images.unsplash.com/photo-1775946477600-3616edfc6c26?w=900"
                alt="Worn-in denim jacket — Boggy's pick"
                className="w-full h-full object-cover"
              />
              <span className="tag-sticker bg-green absolute -bottom-3 left-3 text-sm rotate-tilt-l">Boggy's pick</span>
            </div>
            <div className="brut aspect-square overflow-hidden rotate-2 mt-2 relative" data-testid="hero-fox-pick">
              <img
                src="https://images.unsplash.com/photo-1614990354198-b06764dcb13c?w=900"
                alt="Vintage Levi's 501 jeans on the rack — Fox's pick"
                className="w-full h-full object-cover"
              />
              <span className="tag-sticker bg-orange text-white absolute -bottom-3 right-3 text-sm rotate-tilt-r">Fox's pick</span>
            </div>
          </div>
        </div>
      </section>

      <Marquee words={["boggy says hop", "fox says shop", "thrift hard", "wear loud", "ship fast", "pay direct", "vintage finds"]} />

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="tag-sticker bg-yellow text-xl rotate-tilt-l inline-block mb-2">fresh drops</span>
            <h2 className="font-display text-4xl md:text-6xl uppercase">Just hung up</h2>
          </div>
          <Link to="/shop" className="brut-btn bg-cyan px-5 py-3 hidden md:inline-flex items-center gap-2" data-testid="see-all-btn">See all <ArrowRight size={18} strokeWidth={3} /></Link>
        </div>
        {items.length === 0 ? (
          <div className="brut p-10 text-center">
            <p className="font-display text-3xl uppercase">The rack is empty…</p>
            <p className="mt-2">Boggy is busy folding. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {items.map((it, i) => <ItemCard key={it.id} item={it} idx={i} />)}
          </div>
        )}
      </section>

      {/* WHY */}
      <section className="bg-ink py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 text-[var(--bg)]">
          {[
            { icon: <Recycle size={40} strokeWidth={3} />, title: "Less waste", body: "Boggy hates landfills almost as much as he loves Hawaiian shirts. Buy second, save first." },
            { icon: <Sparkles size={40} strokeWidth={3} />, title: "Real curation", body: "Fox sniffs out the gems. No algorithms — just two animals with taste." },
            { icon: <Heart size={40} strokeWidth={3} />, title: "Direct to us", body: "Your dollars land in our small business bank. No middleman taking a bite." },
          ].map((c, i) => (
            <div key={i} className="bg-paper text-ink brut p-6">
              <div className="mb-3">{c.icon}</div>
              <h3 className="font-display text-2xl uppercase">{c.title}</h3>
              <p className="mt-2">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BOGGY'S PICKS */}
      {boggyItems.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16" data-testid="boggy-section">
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <CuratorBadge curator="boggy" size="lg" />
            <div>
              <span className="tag-sticker bg-green text-xl rotate-tilt-l inline-block mb-1">curated by</span>
              <h2 className="font-display text-3xl md:text-5xl uppercase">Boggy's picks</h2>
              <p className="text-sm md:text-base opacity-70">Loud colors, big patterns, the stuff Boggy would actually wear to the pond.</p>
            </div>
            <div className="ml-auto">
              <Link to="/shop?curator=boggy" className="brut-btn bg-green px-4 py-2 hidden md:inline-flex items-center gap-2" data-testid="see-boggy-btn">Shop Boggy's <ArrowRight size={16} strokeWidth={3} /></Link>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {boggyItems.map((it, i) => <ItemCard key={it.id} item={it} idx={i} />)}
          </div>
        </section>
      )}

      {/* FOX'S PICKS */}
      {foxItems.length > 0 && (
        <section className="bg-paper py-12 md:py-16" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(255,90,0,0.08) 100%)" }} data-testid="fox-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center gap-4 mb-6 flex-wrap">
              <CuratorBadge curator="fox" size="lg" />
              <div>
                <span className="tag-sticker bg-orange text-white text-xl rotate-tilt-r inline-block mb-1">curated by</span>
                <h2 className="font-display text-3xl md:text-5xl uppercase">Fox's picks</h2>
                <p className="text-sm md:text-base opacity-70">Clean denim, perfect tees, the timeless basics. Fox doesn't mess around.</p>
              </div>
              <div className="ml-auto">
                <Link to="/shop?curator=fox" className="brut-btn bg-orange text-white px-4 py-2 hidden md:inline-flex items-center gap-2" data-testid="see-fox-btn">Shop Fox's <ArrowRight size={16} strokeWidth={3} /></Link>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {foxItems.map((it, i) => <ItemCard key={it.id} item={it} idx={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-6">
          <span className="tag-sticker bg-pink text-2xl rotate-tilt-r inline-block mb-3">join the den</span>
          <h2 className="font-display text-4xl md:text-5xl uppercase">Make an account, save your loot.</h2>
          <p className="mt-4 text-lg">Heart your favorites, keep purchase history forever, get email receipts, and check out in two clicks. Boggy will personally tape your invoice.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/signup" className="brut-btn bg-orange text-white px-6 py-3" data-testid="cta-signup">Sign up free</Link>
            <Link to="/shop" className="brut-btn bg-white px-6 py-3" data-testid="cta-browse">Browse first</Link>
          </div>
        </div>
        <div className="md:col-span-6 brut p-6 bg-cyan">
          <h3 className="font-display text-3xl uppercase">First-fit deal</h3>
          <p className="mt-2 text-lg">New here? Use code <span className="font-display bg-ink text-[var(--bg)] px-2">WELCOME5</span> for $5 off any order over $20. Boggy approved.</p>
        </div>
      </section>

      <footer className="border-t-4 border-[#1A1A1A] bg-paper py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-wrap gap-3 justify-between items-center">
          <div className="font-display text-2xl uppercase">Boggy <span className="font-sticker text-orange">&</span> Fox</div>
          <div className="flex gap-4 text-sm font-bold uppercase tracking-wider">
            <Link to="/boggy" className="underline decoration-[3px] decoration-[#00FF66] underline-offset-4" data-testid="footer-boggy">Meet Boggy</Link>
            <Link to="/fox" className="underline decoration-[3px] decoration-[#FF5A00] underline-offset-4" data-testid="footer-fox">Meet Fox</Link>
            <Link to="/shop">Shop</Link>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} Boggy and Fox · Pre-loved threads, picked by a frog & a fox.</p>
        </div>
      </footer>
    </div>
  );
}
